'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Popover, Text, Badge, Spinner, Button } from '@terra/ui-design-system';
import {
  Tick01Icon,
  ArrowDown01Icon,
  CookieIcon,
  Key01Icon,
  SquareLock01Icon,
  PlayCircleIcon,
  RefreshIcon,
  UserIcon,
} from '@terra/ui-icons';
import { useMemo, useCallback, useState, useRef } from 'react';
import type { CachedCredentialDto } from '@terra/shared-contracts-authenticator';
import {
  useCachedCredentials,
  useRefreshCredential,
} from '../../hooks/useCachedCredentials';
import {
  hasReplayInjectableAuthMaterial,
  hasReplayRunAllContext,
} from './credentialDataUtils';
import { applyCredentialToRequestText } from './applyCredentialToRequestText';
import { useApplicationScriptConfig } from '../../store/preRequestScriptStore';

export type ActiveCredential = {
  id: string;
  username: string;
  role: string;
} | null;
export type CredentialWithRequest = {
  credential: CachedCredentialDto;
  requestText: string;
};
type CredentialToolbarDropdownProps = {
  applicationId?: string;
  requestText: string;
  onRequestChange: (text: string) => void;
  activeCredential: ActiveCredential;
  onActiveCredentialChange: (credential: ActiveCredential) => void;
  /** Optional callback to run the request with all cached credentials at once */
  onRunAllCredentials?: (
    credentialsWithRequests: CredentialWithRequest[],
  ) => void;
};

export { applyCredentialToRequestText } from './applyCredentialToRequestText';
export function CredentialToolbarDropdown({
  applicationId,
  requestText,
  onRequestChange,
  activeCredential,
  onActiveCredentialChange,
  onRunAllCredentials,
}: CredentialToolbarDropdownProps) {
  const { credentials, isLoading } = useCachedCredentials({
    applicationId,
    enabled: !!applicationId,
  });
  const { activePreset, script: appScript } =
    useApplicationScriptConfig(applicationId);
  const hasActivePreRequestScript = !!activePreset && !!appScript.trim();
  const refreshCredentialMutation = useRefreshCredential();
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const [refreshedIds, setRefreshedIds] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const requestTextRef = useRef(requestText);

  requestTextRef.current = requestText;
  const applyCredential = useCallback(
    (credential: CachedCredentialDto) => {
      const previousCredential = activeCredential
        ? credentials.find((c) => c.id === activeCredential.id)
        : undefined;
      const newValue = applyCredentialToRequestText({
        requestText: requestTextRef.current,
        credential,
        previousCredential,
      });

      onRequestChange(newValue);
      onActiveCredentialChange({
        id: credential.id,
        username: credential.username,
        role: credential.role,
      });
    },
    [onRequestChange, onActiveCredentialChange, activeCredential, credentials],
  );
  const handleRefreshAndApply = useCallback(
    async (credential: CachedCredentialDto) => {
      if (!applicationId) return;
      setRefreshingIds((prev) => new Set(prev).add(credential.id));
      setRefreshedIds((prev) => {
        const next = new Set(prev);

        next.delete(credential.id);

        return next;
      });

      try {
        const result = await refreshCredentialMutation.mutateAsync({
          applicationId,
          credentialId: credential.id,
          sessionId: `replay-${Date.now()}`,
        });
        // Pass the stale credential as previousCredential so its headers are
        // cleaned up correctly. Bypassing applyCredential avoids a stale
        // closure resolving previousCredential via activeCredential.
        const newValue = applyCredentialToRequestText({
          requestText: requestTextRef.current,
          credential: result.credential,
          previousCredential: credential,
        });

        onRequestChange(newValue);
        onActiveCredentialChange({
          id: result.credential.id,
          username: result.credential.username,
          role: result.credential.role,
        });
        setRefreshedIds((prev) => new Set(prev).add(credential.id));
      } catch (error) {
        console.error('Failed to refresh credential:', error);
      } finally {
        setRefreshingIds((prev) => {
          const next = new Set(prev);

          next.delete(credential.id);

          return next;
        });
      }
    },
    [
      applicationId,
      refreshCredentialMutation,
      onRequestChange,
      onActiveCredentialChange,
    ],
  );
  // Group credentials by whether they have cached auth data
  const { credentialsWithData, credentialsWithoutData } = useMemo(() => {
    const withData: CachedCredentialDto[] = [];
    const withoutData: CachedCredentialDto[] = [];

    credentials.forEach((credential) => {
      if (hasReplayInjectableAuthMaterial({ credential })) {
        withData.push(credential);
      } else {
        withoutData.push(credential);
      }
    });

    return {
      credentialsWithData: withData,
      credentialsWithoutData: withoutData,
    };
  }, [credentials]);
  const credentialsForRunAll = useMemo(
    () =>
      credentials.filter((credential) =>
        hasReplayRunAllContext({ credential, hasActivePreRequestScript }),
      ),
    [credentials, hasActivePreRequestScript],
  );
  const handleRunAllCredentials = useCallback(() => {
    if (!onRunAllCredentials || credentialsForRunAll.length === 0) return;
    const credentialsWithRequests = credentialsForRunAll.map((credential) => ({
      credential,
      requestText: applyCredentialToRequestText({
        requestText: requestTextRef.current,
        credential,
      }),
    }));

    onRunAllCredentials(credentialsWithRequests);
  }, [onRunAllCredentials, credentialsForRunAll]);

  // Don't render if no application selected
  if (!applicationId) {
    return null;
  }

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(details) => {
        setIsOpen(details.open);
        if (!details.open) {
          setRefreshedIds(new Set());
        }
      }}
      positioning={{ placement: 'bottom-end', strategy: 'fixed', gutter: 4 }}
      lazyMount
    >
      <Popover.Trigger asChild>
        <Button
          size="xs"
          variant={activeCredential ? 'subtle' : 'ghost'}
          aria-label="Switch user credentials"
        >
          {isLoading ? (
            <Spinner size="xs" />
          ) : (
            <>
              <UserIcon size={12} />
              {activeCredential && (
                <Text fontSize="xs" fontWeight="medium">
                  {activeCredential.username} ({activeCredential.role})
                </Text>
              )}
              <ArrowDown01Icon size={10} />
            </>
          )}
        </Button>
      </Popover.Trigger>

      <Popover.Positioner>
        <Popover.Content
          minW="260px"
          maxH="320px"
          display="flex"
          flexDirection="column"
          overflow="hidden"
          p="0"
        >
          {/* Header */}
          <Box
            px="3"
            py="2"
            borderBottomWidth="1px"
            borderColor="border.subtle"
            flexShrink={0}
          >
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              Switch User Context
            </Text>
          </Box>

          <Box flex="1" minH="0" overflowY="auto">
            {isLoading ? (
              <Box
                p="3"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Spinner size="sm" />
                <Text ml="2" fontSize="sm" color="fg.muted">
                  Loading...
                </Text>
              </Box>
            ) : credentials.length === 0 ? (
              <Box p="3">
                <Text fontSize="sm" color="fg.muted">
                  No credentials configured.
                </Text>
                <Text fontSize="xs" color="fg.subtle" mt="1">
                  Add credentials in Target Settings.
                </Text>
              </Box>
            ) : (
              <VStack gap="0" alignItems="stretch">
                {/* Credentials with cached auth data */}
                {credentialsWithData.length > 0 && (
                  <Box>
                    <Box px="3" py="1.5">
                      <HStack gap="1">
                        <SquareLock01Icon size={12} />
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color="fg.muted"
                        >
                          Cached Credentials
                        </Text>
                      </HStack>
                    </Box>

                    {credentialsWithData.map((credential) => (
                      <Box
                        key={credential.id}
                        px="3"
                        py="2"
                        cursor="pointer"
                        _hover={{ bg: 'bg.muted' }}
                        onClick={() => {
                          applyCredential(credential);
                          setIsOpen(false);
                        }}
                      >
                        <HStack gap="2" w="full">
                          <UserIcon size={14} />
                          <VStack gap="0" alignItems="flex-start" flex="1">
                            <HStack gap="1">
                              <Text fontSize="sm" fontWeight="medium">
                                {credential.username}
                              </Text>
                              {activeCredential?.id === credential.id && (
                                <Badge variant="emphasis" size="sm">
                                  Active
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="fg.muted">
                              {credential.role}
                            </Text>
                          </VStack>
                          <HStack gap="0.5">
                            {credential.headers.length > 0 && (
                              <Box
                                as="span"
                                title={`${credential.headers.length} header(s)`}
                              >
                                <Key01Icon size={10} />
                              </Box>
                            )}
                            {credential.cookies.length > 0 && (
                              <Box
                                as="span"
                                title={`${credential.cookies.length} cookie(s)`}
                              >
                                <CookieIcon size={10} />
                              </Box>
                            )}
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Run All Credentials option */}
                {onRunAllCredentials && credentialsForRunAll.length > 1 && (
                  <>
                    <Box h="1px" bg="border.subtle" />
                    <Box
                      px="3"
                      py="2"
                      cursor="pointer"
                      _hover={{ bg: 'bg.muted' }}
                      onClick={() => {
                        handleRunAllCredentials();
                        setIsOpen(false);
                      }}
                    >
                      <HStack gap="2" w="full">
                        <PlayCircleIcon size={14} />
                        <VStack gap="0" alignItems="flex-start" flex="1">
                          <Text fontSize="sm" fontWeight="medium">
                            Run All Credentials
                          </Text>
                          <Text fontSize="xs" color="fg.muted">
                            Execute request with all{' '}
                            {credentialsForRunAll.length} cached credentials
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </>
                )}

                {/* Refresh options */}
                <Box h="1px" bg="border.subtle" />
                <Box>
                  <Box px="3" py="1.5">
                    <HStack gap="1">
                      <RefreshIcon size={12} />
                      <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                        Refresh & Apply
                      </Text>
                    </HStack>
                  </Box>

                  {credentials.map((credential) => {
                    const isCurrentRefreshing = refreshingIds.has(
                      credential.id,
                    );
                    const wasRefreshed = refreshedIds.has(credential.id);

                    return (
                      <Box
                        key={`refresh-${credential.id}`}
                        px="3"
                        py="2"
                        cursor={isCurrentRefreshing ? 'not-allowed' : 'pointer'}
                        opacity={isCurrentRefreshing ? 0.5 : 1}
                        _hover={{
                          bg: isCurrentRefreshing ? undefined : 'bg.muted',
                        }}
                        onClick={() => {
                          if (!isCurrentRefreshing) {
                            handleRefreshAndApply(credential);
                          }
                        }}
                      >
                        <HStack gap="2" w="full">
                          {isCurrentRefreshing ? (
                            <Spinner size="xs" />
                          ) : wasRefreshed ? (
                            <Tick01Icon size={14} color="green" />
                          ) : (
                            <RefreshIcon size={14} />
                          )}
                          <VStack gap="0" alignItems="flex-start" flex="1">
                            <Text fontSize="sm">{credential.username}</Text>
                            <Text fontSize="xs" color="fg.muted">
                              {credential.role} -{' '}
                              {wasRefreshed ? 'Done' : 'Re-authenticate'}
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    );
                  })}
                </Box>

                {/* Credentials without data (hint to refresh) */}
                {credentialsWithoutData.length > 0 &&
                  credentialsWithData.length > 0 && (
                    <>
                      <Box h="1px" bg="border.subtle" />
                      <Box>
                        <Box px="3" py="1.5">
                          <Text fontSize="xs" color="fg.subtle">
                            No cached auth data
                          </Text>
                        </Box>

                        {credentialsWithoutData.map((credential) => (
                          <Box
                            key={credential.id}
                            px="3"
                            py="2"
                            opacity={0.5}
                            title="No cached auth data. Use 'Refresh & Apply' above."
                          >
                            <HStack gap="2" w="full">
                              <UserIcon size={14} />
                              <VStack gap="0" alignItems="flex-start" flex="1">
                                <Text fontSize="sm">{credential.username}</Text>
                                <Text fontSize="xs" color="fg.muted">
                                  {credential.role} - No cached auth data
                                </Text>
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
              </VStack>
            )}
          </Box>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
